'use client';
import * as React from 'react';
import PropTypes from 'prop-types';
import { useMergedRefs } from '@base-ui/utils/useMergedRefs';
import { useStore } from '@mui/x-internals/store';
import composeClasses from '@mui/utils/composeClasses';
import { useLicenseVerifier, Watermark } from '@mui/x-license/internals';
import {
  TreeViewProvider,
  RichTreeViewItems,
  TreeViewItemDepthContext,
  itemsSelectors,
  useTreeViewStore,
} from '@mui/x-tree-view/internals';
import { warnOnce } from '@mui/x-internals/warning';
import { styled, createUseThemeProps } from '../internals/zero-styled';
import { getRichTreeViewProUtilityClass } from './richTreeViewProClasses';
import { RichTreeViewProProps } from './RichTreeViewPro.types';
import { useExtractRichTreeViewProParameters } from './useExtractRichTreeViewProParameters';
import { RichTreeViewProStore } from '../internals/RichTreeViewProStore';
import { RichTreeViewVirtualizedItems } from '../components/RichTreeViewVirtualizedItems';
import { virtualizationSelectors } from '../internals/plugins/virtualization';

const useThemeProps = createUseThemeProps('MuiRichTreeViewPro');

const useUtilityClasses = <R extends {}, Multiple extends boolean | undefined>(
  ownerState: RichTreeViewProProps<R, Multiple>,
) => {
  const { classes } = ownerState;

  return React.useMemo(() => {
    const slots = {
      root: ['root'],
      item: ['item'],
      itemContent: ['itemContent'],
      itemGroupTransition: ['itemGroupTransition'],
      itemIconContainer: ['itemIconContainer'],
      itemLabel: ['itemLabel'],
      itemLabelInput: ['itemLabelInput'],
      itemCheckbox: ['itemCheckbox'],
      itemDragAndDropOverlay: ['itemDragAndDropOverlay'],
      itemErrorIcon: ['itemErrorIcon'],
    };

    return composeClasses(slots, getRichTreeViewProUtilityClass, classes);
  }, [classes]);
};

export const RichTreeViewProRoot = styled('ul', {
  name: 'MuiRichTreeViewPro',
  slot: 'Root',
})<{ ownerState: RichTreeViewProProps<any, any> }>({
  padding: 0,
  margin: 0,
  listStyle: 'none',
  outline: 0,
  position: 'relative',
  '&[data-virtualized]': {
    height: '100%',
    width: '100%',
  },
});

type RichTreeViewProComponent = (<R extends {}, Multiple extends boolean | undefined = undefined>(
  props: RichTreeViewProProps<R, Multiple> & React.RefAttributes<HTMLUListElement>,
) => React.JSX.Element) & { propTypes?: any };

const packageInfo = {
  releaseDate: 'MTc4MzAzNjgwMDAwMA==',
  version: process.env.MUI_VERSION!,
  name: 'x-tree-view-pro' as const,
};

/**
 *
 * Demos:
 *
 * - [Tree View](https://mui.com/x/react-tree-view/)
 *
 * API:
 *
 * - [RichTreeView API](https://mui.com/x/api/tree-view/rich-tree-view/)
 */
const RichTreeViewPro = React.forwardRef(function RichTreeViewPro<
  R extends {},
  Multiple extends boolean | undefined = undefined,
>(inProps: RichTreeViewProProps<R, Multiple>, forwardedRef: React.Ref<HTMLUListElement>) {
  const props = useThemeProps({ props: inProps, name: 'MuiRichTreeViewPro' });

  useLicenseVerifier(packageInfo);

  if (process.env.NODE_ENV !== 'production') {
    if ((props as any).children != null) {
      warnOnce([
        'MUI X: The `<RichTreeViewPro />` component does not support JSX children.',
        'If you want to add items, you need to use the `items` prop.',
        'Check the documentation for more details: https://mui.com/x/react-tree-view/rich-tree-view/items/.',
      ]);
    }
  }

  const {
    slots: inSlots,
    slotProps,
    apiRef,
    parameters,
    forwardedProps,
  } = useExtractRichTreeViewProParameters(props);

  if (process.env.NODE_ENV !== 'production') {
    if (parameters.itemHeight === null && !parameters.disableVirtualization) {
      warnOnce([
        'MUI X: `itemHeight={null}` is not compatible with virtualization.',
        'Please use `disableVirtualization` when using `itemHeight={null}`.',
      ]);
    }
  }

  // Context hooks
  const store = useTreeViewStore(RichTreeViewProStore, parameters);

  // Ref hooks
  const ref = React.useRef<HTMLUListElement | null>(null);
  const handleRef = useMergedRefs(forwardedRef, ref);

  // Selector hooks
  const isVirtualizationEnabled = useStore(store, virtualizationSelectors.enabled);

  // Feature hooks
  const classes = useUtilityClasses(props);
  const slots = React.useMemo(() => ({ root: RichTreeViewProRoot, ...inSlots }), [inSlots]);

  const Renderer = isVirtualizationEnabled ? RichTreeViewVirtualizedItems : RichTreeViewItems;

  return (
    <TreeViewProvider
      store={store}
      classes={classes}
      slots={slots}
      slotProps={slotProps}
      apiRef={apiRef}
      rootRef={ref}
    >
      <TreeViewItemDepthContext.Provider value={itemsSelectors.itemDepth}>
        <Renderer
          slots={slots}
          slotProps={slotProps}
          forwardedProps={forwardedProps}
          ownerState={props}
          rootRef={handleRef}
        />
        <Watermark packageInfo={packageInfo} />
      </TreeViewItemDepthContext.Provider>
    </TreeViewProvider>
  );
}) as RichTreeViewProComponent;

RichTreeViewPro.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * The ref object that allows Tree View manipulation. Can be instantiated with `useRichTreeViewApiProRef()`.
   */
  apiRef: PropTypes.shape({
    current: PropTypes.shape({
      focusItem: PropTypes.func,
      getItem: PropTypes.func,
      getItemDOMElement: PropTypes.func,
      getItemOrderedChildrenIds: PropTypes.func,
      getItemTree: PropTypes.func,
      getParentId: PropTypes.func,
      isItemExpanded: PropTypes.func,
      setEditedItem: PropTypes.func,
      setIsItemDisabled: PropTypes.func,
      setItemExpansion: PropTypes.func,
      setItemSelection: PropTypes.func,
      updateItemChildren: PropTypes.func,
      updateItemLabel: PropTypes.func,
    }),
  }),
  /**
   * Used to determine if a given item can move to some new position.
   * @param {object} parameters The params describing the item re-ordering.
   * @param {TreeViewItemId} parameters.itemId The id of the item that is being moved to a new position.
   * @param {TreeViewItemReorderPosition} parameters.oldPosition The old position of the item.
   * @param {TreeViewItemReorderPosition} parameters.newPosition The new position of the item.
   * @returns {boolean} `true` if the item can move to the new position.
   */
  canMoveItemToNewPosition: PropTypes.func,
  /**
   * Whether the Tree View renders a checkbox at the left of its label that allows selecting it.
   * @default false
   */
  checkboxSelection: PropTypes.bool,
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  className: PropTypes.string,
  /**
   * The data source object.
   */
  dataSource: PropTypes.shape({
    getChildrenCount: PropTypes.func.isRequired,
    getTreeItems: PropTypes.func.isRequired,
  }),
  /**
   * The data source cache object.
   */
  dataSourceCache: PropTypes.shape({
    clear: PropTypes.func.isRequired,
    get: PropTypes.func.isRequired,
    set: PropTypes.func.isRequired,
  }),
  /**
   * Expanded item ids.
   * Used when the item's expansion is not controlled.
   * @default []
   */
  defaultExpandedItems: PropTypes.arrayOf(PropTypes.string),
  /**
   * Selected item ids. (Uncontrolled)
   * When `multiSelect` is true this takes an array of strings; when false (default) a string.
   * @default []
   */
  defaultSelectedItems: PropTypes.any,
  /**
   * Whether the items should be focusable when disabled.
   * @default false
   */
  disabledItemsFocusable: PropTypes.bool,
  /**
   * Whether selection is disabled.
   * @default false
   */
  disableSelection: PropTypes.bool,
  /**
   * If `true`, virtualization is disabled.
   * @default false
   */
  disableVirtualization: PropTypes.bool,
  /**
   * When `'flat'`, the tree is rendered as a flat list (children are rendered as siblings of their parent).
   * When `'nested'`, children are rendered inside their parent's groupTransition slot.
   * Collapse/expansion animations that rely on the groupTransition slot only work with the nested DOM structure.
   * Nested DOM structure is not compatible with virtualization.
   * @default 'flat'
   */
  domStructure: PropTypes.oneOf(['flat', 'nested']),
  /**
   * Expanded item ids.
   * Used when the item's expansion is controlled.
   */
  expandedItems: PropTypes.arrayOf(PropTypes.string),
  /**
   * The slot that triggers the item's expansion when clicked.
   * @default 'content'
   */
  expansionTrigger: PropTypes.oneOf(['content', 'iconContainer']),
  /**
   * Used to determine the children of a given item.
   *
   * @template R
   * @param {R} item The item to check.
   * @returns {R[]} The children of the item.
   * @default (item) => item.children
   */
  getItemChildren: PropTypes.func,
  /**
   * Used to determine the id of a given item.
   *
   * @template R
   * @param {R} item The item to check.
   * @returns {TreeViewItemId} The id of the item.
   * @default (item) => item.id
   */
  getItemId: PropTypes.func,
  /**
   * Used to determine the string label for a given item.
   *
   * @template R
   * @param {R} item The item to check.
   * @returns {string} The label of the item.
   * @default (item) => item.label
   */
  getItemLabel: PropTypes.func,
  /**
   * This prop is used to help implement the accessibility logic.
   * If you don't provide this prop. It falls back to a randomly generated id.
   */
  id: PropTypes.string,
  /**
   * Used to determine if a given item should be disabled.
   * @template R
   * @param {R} item The item to check.
   * @returns {boolean} `true` if the item should be disabled.
   */
  isItemDisabled: PropTypes.func,
  /**
   * Determine if a given item can be edited.
   * @template R
   * @param {R} item The item to check.
   * @returns {boolean} `true` if the item can be edited.
   * @default () => false
   */
  isItemEditable: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  /**
   * Determine if a given item can be reordered.
   * @param {TreeViewItemId} itemId The id of the item to check.
   * @returns {boolean} `true` if the item can be reordered.
   * @default () => true
   */
  isItemReorderable: PropTypes.func,
  /**
   * Used to determine if a given item should have selection disabled.
   * @template R
   * @param {R} item The item to check.
   * @returns {boolean} `true` if the item should have selection disabled.
   */
  isItemSelectionDisabled: PropTypes.func,
  /**
   * Horizontal indentation between an item and its children.
   * Examples: 24, "24px", "2rem", "2em".
   * @default 12px
   */
  itemChildrenIndentation: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Sets the height in pixel of an item.
   * Set to `null` to explicitly remove any item height restriction when items have different heights (not compatible with virtualization).
   * @default 32
   */
  itemHeight: PropTypes.number,
  items: PropTypes.array.isRequired,
  /**
   * If `true`, the reordering of items is enabled.
   * @default false
   */
  itemsReordering: PropTypes.bool,
  /**
   * Whether multiple items can be selected.
   * @default false
   */
  multiSelect: PropTypes.bool,
  /**
   * Callback fired when Tree Items are expanded/collapsed.
   * @param {React.SyntheticEvent} event The DOM event that triggered the change. Can be null when the change is caused by the `publicAPI.setItemExpansion()` method.
   * @param {TreeViewItemId[]} itemIds The ids of the expanded items.
   */
  onExpandedItemsChange: PropTypes.func,
  /**
   * Callback fired when the `content` slot of a given Tree Item is clicked.
   * @param {React.MouseEvent} event The DOM event that triggered the change.
   * @param {TreeViewItemId} itemId The id of the focused item.
   */
  onItemClick: PropTypes.func,
  /**
   * Callback fired when a Tree Item is expanded or collapsed.
   * @param {React.SyntheticEvent | null} event The DOM event that triggered the change. Can be null when the change is caused by the `publicAPI.setItemExpansion()` method.
   * @param {TreeViewItemId} itemId The itemId of the modified item.
   * @param {boolean} isExpanded `true` if the item has just been expanded, `false` if it has just been collapsed.
   */
  onItemExpansionToggle: PropTypes.func,
  /**
   * Callback fired when a given Tree Item is focused.
   * @param {React.SyntheticEvent | null} event The DOM event that triggered the change. **Warning**: This is a generic event not a focus event.
   * @param {TreeViewItemId} itemId The id of the focused item.
   */
  onItemFocus: PropTypes.func,
  /**
   * Callback fired when the label of an item changes.
   * @param {TreeViewItemId} itemId The id of the item that was edited.
   * @param {string} newLabel The new label of the items.
   */
  onItemLabelChange: PropTypes.func,
  /**
   * Callback fired when a Tree Item is moved in the tree.
   * @param {object} parameters The params describing the item re-ordering.
   * @param {TreeViewItemId} parameters.itemId The id of the item moved.
   * @param {TreeViewItemReorderPosition} parameters.oldPosition The old position of the item.
   * @param {TreeViewItemReorderPosition} parameters.newPosition The new position of the item.
   */
  onItemPositionChange: PropTypes.func,
  /**
   * Callback fired when a Tree Item is selected or deselected.
   * @param {React.SyntheticEvent} event The DOM event that triggered the change. Can be null when the change is caused by the `publicAPI.setItemSelection()` method.
   * @param {TreeViewItemId} itemId The itemId of the modified item.
   * @param {boolean} isSelected `true` if the item has just been selected, `false` if it has just been deselected.
   */
  onItemSelectionToggle: PropTypes.func,
  /**
   * Callback fired when the children of an item are loaded from the data source.
   * Only relevant for lazy-loaded tree views.
   * @param {object} parameters The parameters of the callback.
   * @param {R[]} parameters.items The items that were loaded.
   * @param {TreeViewItemId | null} parameters.parentId The id of the parent item whose children were loaded. `null` if the root items were loaded.
   * @param {boolean} parameters.isCacheHit `true` if the items were loaded from the cache, `false` if they were fetched from the data source.
   */
  onItemsLazyLoaded: PropTypes.func,
  /**
   * Callback fired when Tree Items are selected/deselected.
   * @param {React.SyntheticEvent} event The DOM event that triggered the change. Can be null when the change is caused by the `publicAPI.setItemSelection()` method.
   * @param {TreeViewItemId[] | TreeViewItemId} itemIds The ids of the selected items.
   * When `multiSelect` is `true`, this is an array of strings; when false (default) a string.
   */
  onSelectedItemsChange: PropTypes.func,
  /**
   * Selected item ids. (Controlled)
   * When `multiSelect` is true this takes an array of strings; when false (default) a string.
   */
  selectedItems: PropTypes.any,
  /**
   * When `selectionPropagation.descendants` is set to `true`.
   *
   * - Selecting a parent selects all its descendants automatically.
   * - Deselecting a parent deselects all its descendants automatically.
   *
   * When `selectionPropagation.parents` is set to `true`.
   *
   * - Selecting all the descendants of a parent selects the parent automatically.
   * - Deselecting a descendant of a selected parent deselects the parent automatically.
   *
   * Only works when `multiSelect` is `true`.
   * On the <SimpleTreeView />, only the expanded items are considered (since the collapsed item are not passed to the Tree View component at all)
   *
   * @default { parents: false, descendants: false }
   */
  selectionPropagation: PropTypes.shape({
    descendants: PropTypes.bool,
    parents: PropTypes.bool,
  }),
  /**
   * The props used for each component slot.
   * @default {}
   */
  slotProps: PropTypes.object,
  /**
   * Overridable component slots.
   * @default {}
   */
  slots: PropTypes.object,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])),
    PropTypes.func,
    PropTypes.object,
  ]),
} as any;

export { RichTreeViewPro };
